import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { initOfflineDB, onNetworkChange } from './utils/offlineStorage';
import { toast } from 'sonner';

// Pages
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import DashboardPage from './pages/DashboardPage';
import VesselSetupPage from './pages/VesselSetupPage';
import ChecklistPage from './pages/ChecklistPage';
import ComplianceResultPage from './pages/ComplianceResultPage';
import CertificatePage from './pages/CertificatePage';
import PublicCertificatePage from './pages/PublicCertificatePage';
import EquipmentExpiryPage from './pages/EquipmentExpiryPage';

// Register service worker for offline support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered:', registration);
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

// Initialize offline database
initOfflineDB().catch(console.error);

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-[#2B3A67] rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v18M19 3v18M5 12h14" />
            </svg>
          </div>
          <div className="animate-spin w-6 h-6 border-2 border-[#2B3A67] border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// Network status indicator
function NetworkStatus() {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  useEffect(() => {
    onNetworkChange((online) => {
      setIsOnline(online);
      if (online) {
        toast.success('Back online! Syncing data...');
      } else {
        toast.warning('You are offline. Changes will sync when connected.');
      }
    });
  }, []);

  if (isOnline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bg-[#E5A000] text-white text-center py-2 text-sm font-bold z-[100]">
      Offline Mode - Changes will sync when connected
    </div>
  );
}

// App Router
function AppRouter() {
  const location = useLocation();

  // Handle OAuth callback - check for session_id in URL fragment
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/certificate/:shareToken" element={<PublicCertificatePage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vessel-setup"
        element={
          <ProtectedRoute>
            <VesselSetupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checklist/:checklistId"
        element={
          <ProtectedRoute>
            <ChecklistPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/result/:checklistId"
        element={
          <ProtectedRoute>
            <ComplianceResultPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/certificate/view/:checklistId"
        element={
          <ProtectedRoute>
            <CertificatePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipment"
        element={
          <ProtectedRoute>
            <EquipmentExpiryPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NetworkStatus />
        <AppRouter />
        <Toaster position="top-center" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
